/**
 * `sync-worker` (W45-F5-S1, #1476) — drains the offline evidence queue to the
 * real evidence-upload endpoint when connectivity returns.
 *
 * Responsibilities:
 *   - Subscribe to the browser `online` event and kick a drain.
 *   - Drain on demand (manual retry from the topbar indicator).
 *   - For each queued item, POST every captured blob via the W6-F2 evidence
 *     upload primitive (`uploadEvidenceXhr` → `/api/v1/proxy/core/buildings/
 *     :id/documents`). On success the item leaves the queue; on failure it
 *     stays `failed` with exponential backoff before the next attempt.
 *   - Publish queue-status snapshots to subscribers (the topbar indicator).
 *
 * The worker is framework-agnostic — no React. The React surface
 * (`useEvidenceSyncStatus`) subscribes to it. Token + online detection are
 * injected so the worker stays unit-testable (mock `navigator.onLine`, mock
 * the uploader).
 */

import {
  uploadEvidenceXhr,
  type UploadedDocument,
} from '@/features/evidence/api/use-upload-evidence';
import {
  getEvidenceQueue,
  type EvidenceQueue,
  type EvidenceQueueItem,
} from './evidence-queue';

export interface EvidenceSyncSnapshot {
  /** Items not yet synced (`pending` + `syncing` + `failed`). */
  readonly pending: number;
  readonly syncing: boolean;
  readonly failed: number;
  /** Whether the browser currently reports an online connection. */
  readonly online: boolean;
}

type Listener = (snapshot: EvidenceSyncSnapshot) => void;

/** The upload function shape — overridable in tests. */
export type EvidenceUploader = (input: {
  buildingId: string;
  file: File;
  description?: string;
  token: string | null;
}) => Promise<UploadedDocument>;

const defaultUploader: EvidenceUploader = (input) =>
  uploadEvidenceXhr({
    buildingId: input.buildingId,
    file: input.file,
    description: input.description,
    token: input.token,
  });

export interface EvidenceSyncWorkerOptions {
  queue?: EvidenceQueue;
  uploader?: EvidenceUploader;
  getAccessToken?: () => Promise<string | null> | string | null;
  /** Test seam — defaults to `navigator.onLine`. */
  isOnline?: () => boolean;
  /** Base backoff in ms; attempt N waits `base * 2^(N-1)`. */
  backoffBaseMs?: number;
  /** Cap on attempts before an item is left `failed` for manual retry. */
  maxAttempts?: number;
}

function defaultIsOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

/**
 * The queue-draining worker. One instance per app (see {@link getEvidenceSyncWorker}).
 */
export class EvidenceSyncWorker {
  private readonly queue: EvidenceQueue;
  private readonly uploader: EvidenceUploader;
  private readonly getAccessToken: () => Promise<string | null> | string | null;
  private readonly isOnline: () => boolean;
  private readonly backoffBaseMs: number;
  private readonly maxAttempts: number;

  private readonly listeners = new Set<Listener>();
  private draining = false;
  private detach: (() => void) | undefined;

  constructor(options: EvidenceSyncWorkerOptions = {}) {
    this.queue = options.queue ?? getEvidenceQueue();
    this.uploader = options.uploader ?? defaultUploader;
    this.getAccessToken = options.getAccessToken ?? (() => null);
    this.isOnline = options.isOnline ?? defaultIsOnline;
    this.backoffBaseMs = options.backoffBaseMs ?? 2000;
    this.maxAttempts = options.maxAttempts ?? 5;
  }

  /**
   * Starts listening for `online` events. Idempotent. Returns a detach fn;
   * also stored so {@link stop} can tear down.
   */
  start(): () => void {
    if (this.detach) return this.detach;
    if (typeof window === 'undefined') {
      this.detach = () => undefined;
      return this.detach;
    }
    const onOnline = (): void => {
      void this.drain();
    };
    window.addEventListener('online', onOnline);
    // Emit an initial snapshot so subscribers render the current pending count.
    void this.publish();
    this.detach = () => {
      window.removeEventListener('online', onOnline);
      this.detach = undefined;
    };
    return this.detach;
  }

  stop(): void {
    this.detach?.();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    // Push the current snapshot immediately so late subscribers aren't blank.
    void this.snapshot().then(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Computes the current queue-status snapshot. */
  async snapshot(): Promise<EvidenceSyncSnapshot> {
    const items = await this.queue.list();
    const failed = items.filter((item) => item.status === 'failed').length;
    return {
      pending: items.length,
      syncing: this.draining,
      failed,
      online: this.isOnline(),
    };
  }

  private async publish(): Promise<void> {
    const snapshot = await this.snapshot();
    for (const listener of this.listeners) listener(snapshot);
  }

  /**
   * Drains the queue once. Concurrent calls collapse into the in-flight pass
   * (no double-drain). Skips entirely when offline. Items that exhaust
   * `maxAttempts` are left `failed` for manual retry.
   */
  async drain(): Promise<void> {
    if (this.draining) return;
    if (!this.isOnline()) {
      await this.publish();
      return;
    }
    this.draining = true;
    await this.publish();
    try {
      const items = await this.queue.list();
      for (const item of items) {
        if (item.status === 'syncing') continue;
        await this.syncItem(item);
      }
    } finally {
      this.draining = false;
      await this.publish();
    }
  }

  /** Manual retry for one failed item — re-arms it and drains. */
  async retry(id: string): Promise<void> {
    await this.queue.markPending(id);
    await this.drain();
  }

  private async syncItem(item: EvidenceQueueItem): Promise<void> {
    await this.queue.markSyncing(item.id);
    await this.publish();
    try {
      const token = await Promise.resolve(this.getAccessToken());
      for (let i = 0; i < item.blobs.length; i += 1) {
        const blob = item.blobs[i];
        if (!blob) continue;
        const filename = item.filenames[i] ?? `evidence-${item.id}-${i}`;
        const file = toFile(blob, filename);
        await this.uploader({
          buildingId: item.buildingId,
          file,
          description: item.note || undefined,
          token,
        });
      }
      await this.queue.markSynced(item.id);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      await this.queue.markFailed(item.id, message);
      // Backoff is advisory — the next `online` event or manual retry drives
      // the re-attempt. We keep the item `failed` rather than spin-looping so
      // a persistent server error can't hot-loop the queue.
    }
  }
}

/**
 * Wraps a Blob as a File so the multipart uploader gets a real filename.
 * Falls back to a Blob cast when `File` is unavailable (very old runtimes).
 */
function toFile(blob: Blob, filename: string): File {
  if (typeof File === 'function') {
    return new File([blob], filename, {
      type: blob.type || 'application/octet-stream',
    });
  }
  // Best-effort fallback: stamp a `name` onto the blob.
  const fallback = blob as Blob & { name?: string };
  fallback.name = filename;
  return fallback as File;
}

let singleton: EvidenceSyncWorker | undefined;

export function getEvidenceSyncWorker(
  options?: EvidenceSyncWorkerOptions,
): EvidenceSyncWorker {
  if (!singleton) singleton = new EvidenceSyncWorker(options);
  return singleton;
}

/** Test seam — resets the singleton between specs. */
export function setEvidenceSyncWorkerForTests(
  worker: EvidenceSyncWorker | undefined,
): void {
  singleton = worker;
}
